import { Stack, Typography, alpha, useTheme } from "@mui/material";
import { toast } from "react-toastify";

import { CommonDialog } from "@/components/common/CommonDialog";
import { PrimaryButton, SecondaryButton } from "@/components/common";
import { usePharmaConsentForPurchase } from "src-next/modules/patient-supporting-documents/hooks/usePharmaConsentForPurchase";

interface DispensaryConsentDialogProps {
  open: boolean;
  patientId: string;
  dispensaryName: string;
  institutionId: string;
  onClose: () => void;
  onGranted: () => void;
}

/** Consentimento individual por dispensador — acionado ao selecionar a dispensação. */
export function DispensaryConsentDialog({
  open,
  patientId,
  dispensaryName,
  institutionId,
  onClose,
  onGranted,
}: DispensaryConsentDialogProps) {
  const theme = useTheme();
  const { isGranting, grantSelected } = usePharmaConsentForPurchase();

  const handleGrant = async () => {
    const result = await grantSelected({
      patientId,
      institutionIds: [institutionId],
    });

    if (result.failed.length > 0 && result.granted.length === 0) {
      toast.error("Não foi possível conceder a autorização.");
      return;
    }

    if (result.failed.length > 0) {
      toast.warning("Autorização parcial — tente novamente.");
      return;
    }

    toast.success("Autorização concedida com sucesso.");
    onGranted();
  };

  const footer = (
    <Stack
      direction={{ xs: "column-reverse", sm: "row" }}
      spacing={1.5}
      justifyContent="flex-end"
      width="100%"
    >
      <SecondaryButton
        width="fit-content"
        height="44px"
        onClick={onClose}
        disabled={isGranting}
      >
        Cancelar
      </SecondaryButton>
      <PrimaryButton
        width={{ xs: "100%", sm: "fit-content" }}
        height="44px"
        onClick={() => void handleGrant()}
        loading={isGranting}
      >
        Autorizar e continuar
      </PrimaryButton>
    </Stack>
  );

  return (
    <CommonDialog
      isOpen={open}
      handleClose={() => {
        if (!isGranting) onClose();
      }}
      maxWidth="sm"
      sheetTitle="Autorização necessária"
      sheetSubtitle=""
      footer={footer}
    >
      <Stack spacing={1.5} sx={{ px: { xs: 0, md: 0.5 }, pt: 0.5 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            px: 1.25,
            py: 0.875,
            borderRadius: 1.5,
            bgcolor: alpha(theme.palette.primary800.main, 0.06),
          }}
        >
          Para solicitar dispensação em <strong>{dispensaryName}</strong>, autorize
          o compartilhamento das suas informações de saúde com este dispensador.
          A autorização permanece válida até revogação manual (LGPD).
        </Typography>
      </Stack>
    </CommonDialog>
  );
}
